import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../../components/ui/dialog';
import {
  Clock,
  UserX,
  Calendar,
  Loader2,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Trash2,
  Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TimeBoundAccessPanel() {
  const { getAuthHeaders } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [daysFilter, setDaysFilter] = useState(30);
  const [showSetExpiryModal, setShowSetExpiryModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [expiryDate, setExpiryDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchExpiringUsers();
  }, [daysFilter]);

  const fetchExpiringUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/expiring-access?days=${daysFilter}`, {
        headers: getAuthHeaders()
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching expiring users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetExpiry = async () => {
    if (!selectedUser || !expiryDate) return;
    
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${selectedUser.id}/set-access-expiry?expiry_date=${encodeURIComponent(expiryDate)}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Access expiry set for ${selectedUser.name}`);
      setShowSetExpiryModal(false);
      setSelectedUser(null);
      setExpiryDate('');
      fetchExpiringUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set access expiry');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveExpiry = async (userId, userName) => {
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}/remove-access-expiry`, {
        headers: getAuthHeaders()
      });
      toast.success(`Access expiry removed for ${userName}`);
      fetchExpiringUsers();
    } catch (error) {
      toast.error('Failed to remove access expiry');
    }
  };

  const openSetExpiryModal = (user) => {
    setSelectedUser(user);
    // Default to 30 days from now
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 30);
    setExpiryDate(defaultDate.toISOString().slice(0, 16));
    setShowSetExpiryModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-red-500" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Time-Bound Access Management</h3>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-slate-600 dark:text-slate-400">Show expiring within:</Label>
          <select
            value={daysFilter}
            onChange={(e) => setDaysFilter(parseInt(e.target.value))}
            className="bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded px-3 py-1 text-slate-900 dark:text-slate-100"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
          </select>
          <Button variant="outline" size="sm" onClick={fetchExpiringUsers}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-4 flex items-start gap-3">
        <Shield className="w-5 h-5 text-amber-500 mt-0.5" />
        <div>
          <h4 className="font-medium text-amber-200">Inspector Time-Limited Access</h4>
          <p className="text-sm text-amber-300/80">
            Set expiry dates for inspector accounts to comply with 21 CFR Part 11 requirements. 
            Users with expired access will be automatically blocked from logging in.
          </p>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-6 border border-slate-300 dark:border-slate-600">
        <div className="flex items-center justify-between mb-4">
          <span className="text-slate-600 dark:text-slate-400">
            {users.length} user(s) with time-limited access
          </span>
        </div>

        {users.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500 opacity-50" />
            <p className="text-lg font-medium">No Time-Limited Users</p>
            <p className="text-sm">No users have time-bound access configured</p>
          </div>
        ) : (
          <ScrollArea className="h-80">
            <div className="space-y-3">
              {users.map((user) => (
                <div 
                  key={user.id} 
                  className={`flex items-center justify-between p-4 rounded-lg ${
                    user.is_expired 
                      ? 'bg-red-500/20 border border-red-500/30' 
                      : user.days_until_expiry <= 7 
                        ? 'bg-amber-500/20 border border-amber-500/30'
                        : 'bg-slate-200 dark:bg-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      user.is_expired ? 'bg-red-500' : 'bg-slate-600'
                    }`}>
                      {user.is_expired ? (
                        <UserX className="w-5 h-5 text-white" />
                      ) : (
                        <Clock className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 dark:text-slate-100">{user.name}</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{user.email}</p>
                      <p className="text-xs text-slate-500">
                        Role: <Badge variant="outline" className="ml-1">{user.role_display_name || user.role}</Badge>
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      {user.is_expired ? (
                        <Badge variant="destructive" className="flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          EXPIRED
                        </Badge>
                      ) : (
                        <Badge variant={user.days_until_expiry <= 7 ? 'warning' : 'secondary'}>
                          {user.days_until_expiry} days left
                        </Badge>
                      )}
                      <p className="text-xs text-slate-500 mt-1">
                        Expires: {new Date(user.permissions?.access_expires_at).toLocaleDateString()}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openSetExpiryModal(user)}
                        className="text-blue-600"
                      >
                        <Calendar className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRemoveExpiry(user.id, user.name)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>

      {/* Set Expiry Modal */}
      <Dialog open={showSetExpiryModal} onOpenChange={setShowSetExpiryModal}>
        <DialogContent className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-red-500" />
              Set Access Expiry
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              Set a time-limited access expiry date for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700 dark:text-slate-300">Expiry Date & Time</Label>
              <Input
                type="datetime-local"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                className="bg-white dark:bg-slate-700 mt-1"
              />
            </div>
            <p className="text-sm text-slate-500">
              After this date, the user will not be able to access the system.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetExpiryModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSetExpiry} disabled={saving || !expiryDate} className="bg-red-600 hover:bg-red-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Set Expiry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
